import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  Tooltip,
  TextField,
  InputAdornment,
  useTheme,
  useMediaQuery,
  Pagination,
  FormControl,
  MenuItem,
  Select,
} from "@mui/material";
import { Delete, Visibility } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import useFetchObjects from "../../../../../api/useFetchObjects";
import useDelete from "../../../../../api/useDelete";
import { useAuth } from "../../../../../context";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const ListReturnMedicine = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const navigate = useNavigate();

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  // Pagination States
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalReturns, setTotalReturns] = useState(0);

  // Fetch Return Medicines with Pagination
  const { data, refetch, isLoading, isError, isSuccess, error } =
    useFetchObjects(
      ["return-medicines"],
      `return-medicines/?&search=${encodeURIComponent(
        searchQuery,
      )}&page=${page}&page_size=${rowsPerPage}`,
      token,
    );

  // Update Total Returns from API Response
  useEffect(() => {
    if (data && typeof data.count === "number") {
      setTotalReturns(data.count);
    }
  }, [data]);

  // Refetch data when dependencies change
  useEffect(() => {
    refetch();
  }, [refetch, searchQuery, page, rowsPerPage]);

  // Delete Handler
  const { handleDelete, ConfirmDialog } = useDelete("return-medicines", token);

  // Dialog and Snackbar States
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedReturn, setSelectedReturn] = useState(null);

  const handleOpenDialog = (medicineReturn) => {
    setSelectedReturn(medicineReturn);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedReturn(null);
  };

  // Pagination Handlers
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1); // Reset to first page
  };

  // Extract return medicines from paginated data
  const returnMedicines = data?.results || [];

  return (
    <Box sx={{ width: "100%" }}>
      <Paper
        elevation={2}
        sx={{
          padding: 3,
          margin: "2em auto",
          maxWidth: "1200px",
          borderRadius: 4,
          boxShadow: "0px 10px 20px rgba(0, 0, 0, 0.1)",
        }}
      >
        <Box
          sx={{
            mb: 1,
            display: "flex",
            justifyContent: "space-between",
            flexDirection: isSmallScreen ? "column" : "row",
            gap: 2,
          }}
        >
          <Typography
            variant="h5"
            fontWeight={600}
            textAlign="center"
            gutterBottom
          >
            {t("pharmacy.returnMedicineList")}
          </Typography>
          {/* Add New Button */}
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/pharmacy/return-medicine/add")}
            sx={{
              textTransform: "none",
              fontWeight: "bold",
              paddingX: 3,
              paddingY: isSmallScreen ? 1 : 1.5,
              alignSelf: isSmallScreen ? "flex-end" : "center",
              borderRadius: "8px",
              boxShadow: 3,
              transition: "all 0.3s ease",
              backgroundColor: theme.palette.primary,
              "&:hover": {
                backgroundColor: theme.palette.primary,
                boxShadow: 6,
                transform: "translateY(-2px)",
              },
              "&:active": {
                boxShadow: 2,
                transform: "translateY(0px)",
              },
              "& .MuiButton-startIcon": {
                marginRight: 1,
              },
            }}
          >
            {t("pharmacy.addNew")}
          </Button>
        </Box>
        <Grid item xs={12} mb={1}>
          {/* Search Bar */}
          <TextField
            variant="outlined"
            size="small"
            placeholder={t("pharmacy.search")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              "aria-label": t("pharmacy.search"),
            }}
            sx={{
              flex: isSmallScreen ? "none" : "2 1 auto",
              width: isSmallScreen ? "100%" : "auto",
            }}
          />
        </Grid>
        <Divider sx={{ marginY: 2 }} />

        {isLoading ? (
          <Grid container justifyContent="center" sx={{ mt: 4 }}>
            <CircularProgress />
          </Grid>
        ) : isError ? (
          <Typography color="error" textAlign="center">
            {t("pharmacy.failedToLoad")}
          </Typography>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead sx={{ backgroundColor: "#f9f9f9" }}>
                  <TableRow>
                    <TableCell align="center">#</TableCell>
                    <TableCell align="center">{t("pharmacy.date")}</TableCell>
                    <TableCell align="center">
                      {t("pharmacy.totalAmount")}
                    </TableCell>
                    <TableCell align="center">
                      {t("pharmacy.actions")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {returnMedicines.length > 0 ? (
                    returnMedicines.map((medicineReturn, index) => (
                      <TableRow key={medicineReturn.id} hover>
                        <TableCell align="center">
                          {(page - 1) * rowsPerPage + index + 1}
                        </TableCell>
                        <TableCell align="center">
                          {medicineReturn.date}
                        </TableCell>
                        <TableCell align="center">
                          {medicineReturn.total_amount}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title={t("pharmacy.viewDetails")}>
                            <IconButton
                              color="primary"
                              onClick={() => handleOpenDialog(medicineReturn)}
                            >
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t("pharmacy.deleteReturn")}>
                            <IconButton
                              color="error"
                              onClick={() => handleDelete(medicineReturn.id)}
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography variant="h6">
                          {t("pharmacy.noMedicinesFound")}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination Controls */}
            <Grid item xs={12}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mt={4}
                mb={2}
              >
                {/* Rows Per Page Selector */}
                <Box display="flex" alignItems="center">
                  <Typography variant="body2" mr={2}>
                    {t("pharmacy.rowsPerPage")}:
                  </Typography>
                  <FormControl variant="outlined" size="small">
                    <Select
                      value={rowsPerPage}
                      onChange={handleRowsPerPageChange}
                      sx={{
                        width: 80,
                        "& .MuiOutlinedInput-notchedOutline": {
                          border: "none",
                        },
                      }}
                    >
                      {[10, 25, 50, 100].map((rows) => (
                        <MenuItem key={rows} value={rows}>
                          {rows}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {/* Pagination Component */}
                <Pagination
                  count={Math.ceil(totalReturns / rowsPerPage)}
                  page={page}
                  onChange={handlePageChange}
                  variant="outlined"
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Box>
            </Grid>
          </>
        )}
      </Paper>

      {/* View Details Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t("pharmacy.returnDetails")}</DialogTitle>
        <DialogContent dividers>
          {selectedReturn ? (
            <>
              <Typography variant="body1" fontWeight={600}>
                {t("pharmacy.id")}: {selectedReturn.id}
              </Typography>
              <Typography variant="body1">
                {t("pharmacy.date")}: {selectedReturn.date}
              </Typography>
              <Typography variant="body1">
                {t("pharmacy.totalAmount")}: {selectedReturn.total_amount}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6">{t("pharmacy.items")}</Typography>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>{t("pharmacy.category")}</TableCell>
                    <TableCell>{t("pharmacy.medicineName")}</TableCell>
                    <TableCell align="center">
                      {t("pharmacy.quantity")}
                    </TableCell>
                    <TableCell align="center">
                      {t("pharmacy.pricePerUnit")}
                    </TableCell>
                    <TableCell align="center">
                      {t("pharmacy.totalPrice")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedReturn.items.map((item, index) => (
                    <TableRow key={item.id}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{item.medicine_category}</TableCell>
                      <TableCell>{item.medicine_name}</TableCell>
                      <TableCell align="center">{item.quantity}</TableCell>
                      <TableCell align="center">{item.price}</TableCell>
                      <TableCell align="center">{item.total_price}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          ) : (
            <Typography>{t("pharmacy.noDetailsAvailable")}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDialog}
            variant="contained"
            color="primary"
          >
            {t("pharmacy.close")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={!!snackbarMessage}
        autoHideDuration={6000}
        onClose={() => setSnackbarMessage("")}
        message={snackbarMessage}
      />

      {/* Confirm Delete Dialog */}
      <ConfirmDialog />
    </Box>
  );
};

export default ListReturnMedicine;
